import {
  Alert,
  Button,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  showNotification,
  type FormControl,
  type FormSubmitHandler,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';
import {useWatch, type UseFormReturn} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {
  newVehicleApi,
  useApplyMidYearPriceAdjustmentMutation,
  type ModelYearResponseBody,
  type PriceAdjustmentRequestBody,
  type PricelistVersionRequestBody,
  type PriceAdjustmentResponseBody,
} from '@dms/api/new-vehicle';
import {useTenant} from '@dms/shared';

import {useDebouncedCallback, useOnMount, type ApiError} from 'shared/web';

import {
  catalogueErrorCodes,
  catalogueErrorMessages,
  handleCatalogueError,
  isStaleCatalogueVersion,
  t,
} from './catalogueUi';
import {PricedItemChangeForm} from './components/PricedItemChangeForm';
import {PricelistVersionFields} from './components/PricelistVersionFields';
import {nextPricelistVersionLabel} from './nextPricelistVersionLabel';
import {PriceChangesTable} from './PriceChangesTable';
import type {PricedItemChange} from './types/PricedItemChange';
import {pricelistVersionSchema, type PricelistVersionValues} from './utils/pricelistVersionSchema';

const DUPLICATE_PRICELIST_LABEL = 'DMS-NV-CT-0032';
const PREVIEW_DELAY_MS = 300;

type PricelistVersionFormHandle = UseFormReturn<PricelistVersionValues>;
type PreviewItems = PriceAdjustmentResponseBody['preview'];
/** The latest answer for a label; `items` stay from an earlier label when this one was refused. */
type Preview = {label: string; items?: PreviewItems; isRefused?: boolean};

/** A price change on a published model year, waiting for the pricelist version it creates. */
export type PublishedPriceChange = {
  mode: 'adjustment';
  adjustment: Pick<PriceAdjustmentRequestBody, 'kind' | 'value' | 'scope' | 'resourceIds'>;
  /** As read after any save that came before the confirm, so its version is current. */
  modelYear: ModelYearResponseBody;
  /** What the save before the confirm already stored. */
  saved?: 'otherChanges' | 'costs';
  /** Closes the editor: after the adjustment, after a stale version, or on a cancel after a save. */
  onDone: () => void;
};

/** The rows of one change of a batch, under its title; `key` is the key of its entry. */
export type PricedItemChangeGroup = {key: string; title: string; items: PricedItemChange[]};

/** A create, archive or inclusion switch of a priced item on a published model year, held until confirmed (D88). */
export type PricedItemChangeRequest = {
  mode: 'createRemove';
  /** Distinguishes requests of one hook, also when they show the same label. */
  id: number;
  modelYear: ModelYearResponseBody;
  /** Every row of the request; the count and Confirm read it. */
  items: PricedItemChange[];
  /** A batch of several changes lists its rows under one heading per change; `items` holds them all. */
  groups?: PricedItemChangeGroup[];
  /** Sends the write, or the batch, with the version. Rejects with the ApiError. */
  submit: (pricelistVersion: PricelistVersionRequestBody) => Promise<unknown>;
  /**
   * A group's refusal that names one of its changes: the message is shown in the dialog, which
   * stays open so the change can be removed and the rest confirmed again.
   */
  failureMessage?: (error: ApiError) => string | undefined;
  /** Shows "Remove from group" under each heading of `groups`; the key is the key of the group. */
  onRemoveEntry?: (key: string) => void;
  /** Each of these settles the request once and closes its dialog; later calls do nothing. */
  onWritten: () => void;
  /** Closed without sending anything. */
  onCancelled: () => void;
  /** A refusal the dialog does not show; the caller shows it, on its own form where it has one. */
  onFailed: (error: ApiError) => void;
};

export type NewPricelistVersionDialogProps = {
  change: PublishedPriceChange | PricedItemChangeRequest | undefined;
  onClose: () => void;
};

/**
 * The one confirmation for every price change on a published model year. An adjustment is previewed
 * for the label shown, then applied under that label with an optional note; a create or remove
 * lists the prices it adds or takes away and sends its write with the label. It stays open after
 * a refusal of the label (say a used one), so the label can be corrected and the change retried.
 */
export function NewPricelistVersionDialog(props: NewPricelistVersionDialogProps) {
  // While Confirm waits for the server, nothing closes the dialog: its answer decides.
  const [isApplying, setApplying] = useState(false);
  const closeAdjustment = (isDone: boolean) => {
    props.onClose();
    if (props.change?.mode === 'adjustment' && (isDone || props.change.saved)) {
      props.change.onDone();
    }
  };
  const cancel = () => {
    if (isApplying) {
      return;
    }
    if (props.change?.mode === 'createRemove') {
      props.change.onCancelled();
      return;
    }
    closeAdjustment(false);
  };
  return (
    <Dialog
      isOpen={Boolean(props.change)}
      onClose={cancel}
      hideCloseButton={isApplying}
      size="large"
      title={t(
        'entity.newVehicle.catalogue.publishedPriceChange.title',
        'Create a new pricelist version'
      )}
    >
      <Show when={props.change}>
        {(change) =>
          change.mode === 'createRemove' ? (
            // Keyed by the request: the next write of a sequence gets a fresh form, also for the same label.
            <PricedItemChangeForm
              key={change.id}
              change={change}
              isApplying={isApplying}
              onApplyingChange={setApplying}
              onCancel={cancel}
            />
          ) : (
            <PricelistVersionForm
              change={change}
              isApplying={isApplying}
              onApplyingChange={setApplying}
              onClose={closeAdjustment}
            />
          )
        }
      </Show>
    </Dialog>
  );
}

function PricelistVersionForm(props: {
  change: PublishedPriceChange;
  isApplying: boolean;
  onApplyingChange: (isApplying: boolean) => void;
  onClose: (isDone: boolean) => void;
}) {
  const dispatch = useDispatch();
  const [previewPrices] = useApplyMidYearPriceAdjustmentMutation();
  const [applyPrices] = useApplyMidYearPriceAdjustmentMutation();
  const [preview, setPreview] = useState<Preview>();
  const latestLabel = useRef<string | undefined>(undefined);
  const isApplying = useRef(false);

  const request = (label: string, previewOnly: boolean, note?: string) => ({
    modelYearId: props.change.modelYear.id,
    priceAdjustmentRequestBody: {
      ...props.change.adjustment,
      previewOnly,
      note,
      nextPricelistVersionLabel: label,
      version: props.change.modelYear.version,
    },
  });
  const refuseLabel = (label: string) =>
    setPreview((current) => ({label, items: current?.items, isRefused: true}));
  /** A used label goes on its field; a stale version reloads and closes, as the editors do. */
  const handleRefusal = (
    error: ApiError,
    label: string,
    setLabelError: (message: string) => void
  ) => {
    if (catalogueErrorCodes(error).includes(DUPLICATE_PRICELIST_LABEL)) {
      refuseLabel(label);
      setLabelError(catalogueErrorMessages(error).join('\n'));
      return;
    }
    handleCatalogueError(error);
    if (isStaleCatalogueVersion(error)) {
      dispatch(
        newVehicleApi.util.invalidateTags(['NewVehicleCatalogue', 'NewVehicleCatalogueAuthoring'])
      );
      props.onClose(true);
    }
  };
  const runPreview = (form: PricelistVersionFormHandle, rawLabel: string) => {
    const label = rawLabel.trim();
    latestLabel.current = label;
    if (!label) {
      return;
    }
    void previewPrices(request(label, true))
      .unwrap()
      .then((response) => {
        if (latestLabel.current === label) {
          form.clearErrors('nextVersion');
          setPreview({label, items: response.preview});
        }
      })
      .catch((error: ApiError) => {
        if (latestLabel.current === label) {
          refuseLabel(label);
          handleRefusal(error, label, (message) => form.setError('nextVersion', {message}));
        }
      });
  };
  const previewLater = useDebouncedCallback(runPreview, PREVIEW_DELAY_MS);
  const confirm: FormSubmitHandler<PricelistVersionValues> = async (values, setErrors) => {
    if (isApplying.current) {
      return;
    }
    isApplying.current = true;
    props.onApplyingChange(true);
    await applyPrices(request(values.nextVersion, false, values.note || undefined))
      .unwrap()
      .then(() => {
        showNotification.success(
          t(
            'entity.newVehicle.catalogue.adjustment.applied',
            'Prices updated. Pricelist is now {label}.',
            {label: values.nextVersion}
          )
        );
        props.onClose(true);
      })
      .catch((error: ApiError) =>
        handleRefusal(error, values.nextVersion, (message) =>
          setErrors([{name: 'nextVersion', message}])
        )
      )
      .finally(() => {
        isApplying.current = false;
        props.onApplyingChange(false);
      });
  };

  return (
    <Form<PricelistVersionValues>
      experimentalZodSchema={pricelistVersionSchema}
      // A label is checked as it is typed, so a disabled Confirm always shows why on the field,
      // and the field error is the only feedback: no generic warning on a refused submit.
      mode="onChange"
      shouldHideInvalidSubmitNotification
      defaultValues={{
        nextVersion: nextPricelistVersionLabel(props.change.modelYear.pricelistVersionLabel),
        note: '',
      }}
      onSubmit={confirm}
    >
      {(control, form) => (
        <VStack spacing={4}>
          <Show when={props.change.saved}>
            {(saved) => (
              <Alert
                variant="success"
                message={
                  saved === 'costs'
                    ? t(
                        'entity.newVehicle.catalogue.publishedPriceChange.costsSaved',
                        'Costs are updated. The price change needs a new pricelist version.'
                      )
                    : t(
                        'entity.newVehicle.catalogue.publishedPriceChange.otherChangesSaved',
                        'Your other changes are saved. The price change needs a new pricelist version.'
                      )
                }
              />
            )}
          </Show>
          <Text size="small">
            {t(
              'entity.newVehicle.catalogue.publishedPriceChange.hint',
              'This model year is published, so the price change gets its own pricelist version and a History entry.'
            )}
          </Text>
          <PreviewList
            modelYearId={props.change.modelYear.id}
            preview={preview}
            onMount={() => runPreview(form, form.getValues('nextVersion'))}
          />
          <PricelistVersionFields
            control={control}
            currentLabel={props.change.modelYear.pricelistVersionLabel}
            onLabelChange={(label) => previewLater(form, label ?? '')}
          />
          <HStack justify="flex-end" spacing={2}>
            <Button
              variant="secondary"
              title={t('general.actions.cancel', 'Cancel')}
              isDisabled={props.isApplying}
              onClick={() => props.onClose(false)}
            />
            <ConfirmButton control={control} preview={preview} />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}

function PreviewList(props: {
  modelYearId: string;
  preview: Preview | undefined;
  onMount: () => void;
}) {
  const {tenantCurrency} = useTenant();
  useOnMount(props.onMount);
  const items = props.preview?.items;
  return (
    <DataStatus
      isLoading={!items && !props.preview?.isRefused}
      isError={!items && props.preview?.isRefused}
      isEmpty={items?.length === 0}
      emptyMessage={t(
        'entity.newVehicle.catalogue.publishedPriceChange.noChanges',
        'No price changes'
      )}
      minHeight={20}
    >
      <VStack spacing={2}>
        <Text size="small" alternative>
          {t(
            'entity.newVehicle.catalogue.adjustment.previewTitle',
            'Prices that will change: {count}',
            {count: items?.length ?? 0}
          )}
        </Text>
        <Box maxHeight={80} overflowY="auto">
          <PriceChangesTable
            currency={tenantCurrency}
            modelYearId={props.modelYearId}
            changes={items ?? []}
          />
        </Box>
      </VStack>
    </DataStatus>
  );
}

/** Enabled only by a preview of the label shown that lists at least one price. */
function ConfirmButton(props: {
  control: FormControl<PricelistVersionValues>;
  preview: Preview | undefined;
}) {
  // A cleared text field holds null.
  const label: string | null = useWatch({control: props.control, name: 'nextVersion'});
  const isPreviewed =
    props.preview?.label === (label ?? '').trim() &&
    !props.preview.isRefused &&
    Boolean(props.preview.items?.length);
  return (
    <FormButton
      control={props.control}
      type="submit"
      isDisabled={!isPreviewed}
      title={t('general.actions.confirm', 'Confirm')}
    />
  );
}
